import React, { useState } from 'react';
import {
    TextField,
    Grid,
    Button,
    FormHelperText,
    useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import SingleSelectionDropdown from '../dropdown/SingleSelectionDropdown';
import { CKEditor } from 'ckeditor4-react';
import { eventCalendarService } from '../../services';
import AlertDialog from '../AlertDialog/AlertDialog';
import moment from 'moment';
import { darkenColor } from '../../utils/appHelper';

const EventForm = (props) => {
    const [formData, setFormData] = useState({
        Title: '',
        Description: '',
        Location: '',
        PointOfContact: '',
        DressCode: '',
        StartDate: props.selectedDate,
        StartTime: null,
        EndDate: null,
        EndTime: null,
        Type: null,
        Color: null,
        ...props.isEdit && props.payload
    });

    const [errors, setErrors] = useState({}); // State for error handling
    const [showAlert, setShowAlert] = useState(false); // State for confirmation dialog handling
    const theme = useTheme(); // Get the current theme
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleDateChange = (date, field) => {
        setFormData((prevState) => ({
            ...prevState,
            [field]: moment(new Date(date)).format('YYYY-MM-DD'),
        }));
    };

    const handleTimeChange = (time, field) => {
        setFormData((prevState) => ({
            ...prevState,
            [field]: moment(new Date(time)).format('HH:mm:ss'),
        }));
    };

    const handleEventTypeChange = (event) => {
        const value = event.target.value;
        const color = props?.eventTypes?.find((item) => item.Id === value)?.Color || null;
        setFormData((prevState) => ({
            ...prevState,
            Type: value,
            Color: color,
        }));
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.Title.trim()) newErrors.Title = "Title is required";
        if (!formData.Type) newErrors.Type = "Event Type is required";
        if (!formData.StartDate) newErrors.StartDate = "Start Date is required";

        // Check if EndDate is earlier than StartDate
        if (formData.StartDate && formData.EndDate && formData.EndDate < formData.StartDate) {
            newErrors.EndDate = "End Date cannot be earlier than Start Date";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowAlert(true);
        }
    };

    const save = async () => {
        const response = await eventCalendarService.publishCalendarEvent(formData);
        if (response && props.onSuccess) {
            props.onSuccess(response);
        }
    }

    const update = async () => {
        const response = await eventCalendarService.updateCalendarEvent(formData);
        if (response && props.onSuccess) {
            props.onSuccess(response);
        }
    }

    const combineDateAndTime = (timeString) => {
        try {
            if (!timeString) return null;
            const [hours, minutes, seconds] = timeString.split(':').map(Number);
            const now = new Date();
            now.setHours(hours, minutes, seconds || 0, 0);
            return moment(now);
        } catch (error) {
            return null;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* Title */}
                    <Grid size={12}>
                        <TextField
                            label="Title"
                            name="Title"
                            variant="outlined"
                            fullWidth
                            value={formData.Title}
                            onChange={handleChange}
                            error={!!errors.Title}
                            helperText={errors.Title}
                        />
                    </Grid>

                    {/* Description */}
                    <Grid size={12}>
                        <CKEditor
                            key={theme.palette.mode} // forces re-mount when light/dark mode changes
                            config={{
                                height: 200,
                                resize_enabled: false,
                                removePlugins: 'elementspath',
                                extraPlugins: '',
                                toolbarCanCollapse: true,
                                contentsCss: `body { max-height: 200px; overflow-y: auto; background-color: ${theme.palette.background.paper}; color: ${theme.palette.text.primary};}`,
                                on: {
                                    instanceReady: function () {
                                        // This is where we apply inline CSS to the toolbar once the editor is ready
                                        const toolbar = this.container.$.querySelector('.cke_top'); // Target the toolbar
                                        if (toolbar) {
                                            toolbar.style.backgroundColor = darkenColor(theme.palette.background.default, 5); // Set the toolbar background color
                                            toolbar.style.color = theme.palette.text.primary; // Set the text color
                                        }
                                        const footer = this.container.$.querySelector('.cke_bottom'); // Target the toolbar
                                        if (footer) {
                                            footer.style.backgroundColor = darkenColor(theme.palette.background.default, 5); // Set the toolbar background color
                                            footer.style.color = theme.palette.text.primary; // Set the text color
                                        }
                                    }
                                }
                            }}
                            data={formData.Description}
                            onChange={(event) =>
                                setFormData(prevState => ({
                                    ...prevState,
                                    Description: event.editor.getData()
                                }))
                            }
                        />
                    </Grid>

                    {/* Location */}
                    <Grid size={12}>
                        <TextField
                            label="Location"
                            name="Location"
                            variant="outlined"
                            fullWidth
                            value={formData.Location}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Event Type */}
                    <Grid size={12}>
                        <SingleSelectionDropdown
                            title={'Select Event Type'}
                            value={formData.Type}
                            dropdownData={props.eventTypes || []}
                            handleValueChange={handleEventTypeChange}
                            keys={{ id: 'Id', value: 'Id', text: 'Text' }}
                            style={{ width: '100%' }}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                            viewSx={{ width: '100%' }}
                        />
                        {errors.Type && <FormHelperText error>{errors.Type}</FormHelperText>}
                    </Grid>

                    {/* Start Date */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="Start Date"
                            slotProps={{ textField: { variant: 'outlined', fullWidth: true, error: !!errors.StartDate } }}
                            value={formData.StartDate ? moment(formData.StartDate) : null}
                            onChange={(date) => handleDateChange(date, 'StartDate')}
                            disablePast
                            format="MM-DD-YYYY"
                        />
                        {errors.StartDate && <FormHelperText error>{errors.StartDate}</FormHelperText>}
                    </Grid>

                    {/* Start Time */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TimePicker
                            label="Start Time"
                            slotProps={{ textField: { variant: 'outlined', fullWidth: true } }}
                            value={combineDateAndTime(formData.StartTime)}
                            onChange={(time) => handleTimeChange(time, 'StartTime')}
                        />
                    </Grid>

                    {/* End Date */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="End Date"
                            slotProps={{ textField: { variant: 'outlined', fullWidth: true, error: !!errors.EndDate } }}
                            value={formData.EndDate ? moment(formData.EndDate) : null}
                            onChange={(date) => handleDateChange(date, 'EndDate')}
                            disablePast
                            format="MM-DD-YYYY"
                        />
                        {errors.EndDate && <FormHelperText error>{errors.EndDate}</FormHelperText>}

                    </Grid>

                    {/* End Time */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TimePicker
                            label="End Time"
                            slotProps={{ textField: { variant: 'outlined', fullWidth: true } }}
                            value={combineDateAndTime(formData.EndTime)}
                            onChange={(time) => handleTimeChange(time, 'EndTime')}
                        />
                    </Grid>

                    {/* Point of Contact */}
                    <Grid size={12}>
                        <TextField
                            label="Point of Contact"
                            name="PointOfContact"
                            variant="outlined"
                            fullWidth
                            value={formData.PointOfContact}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Dress Code */}
                    <Grid size={12}>
                        <TextField
                            label="Dress Code"
                            name="DressCode"
                            variant="outlined"
                            fullWidth
                            value={formData.DressCode}
                            onChange={handleChange}
                        />
                    </Grid>
                </Grid>

                <Grid container justifyContent="flex-end" style={{ marginTop: '30px' }}>
                    <Button type="submit" color="primary" variant="contained">
                        {props?.isEdit ? 'Update' : 'Publish'}
                    </Button>
                </Grid>
                <AlertDialog
                    title={`${props?.isEdit ? 'Update' : 'Publish'} Event`}
                    description={`Are you sure you want to ${props?.isEdit ? 'update' : 'publish'} this event?`}
                    buttonTitle={'Yes'}
                    showAlert={showAlert}
                    onButtonAction={() => props?.isEdit ? update() : save()}
                    addCancel={true}
                    onClose={() => setShowAlert(false)}
                />
            </form>
        </LocalizationProvider>
    );
};

export default EventForm;
